import React from 'react';

/**
 * Reusable contact form fields. Renders the same inputs that the
 * Account → Contacts modal uses, so any other surface that edits a
 * contact (e.g. the Influence Map tab) can share one source.
 *
 * Props:
 *   - contactForm, setContactForm: controlled form state
 *   - leadSources, repFirms, users: reference data for dropdowns
 *   - hideInfluenceFields (optional): when true, omits the four
 *     map-related fields (influence_role, social_style,
 *     relationship_status, disposition) — useful when those fields
 *     live on a separate "Map" tab so they aren't shown twice.
 *   - accounts (optional): [{ id, name }]. When given, an Account select
 *     is shown (bound to contactForm.account_id) so the person can put
 *     the contact on a different account than the page they are on.
 *
 * NOT wrapped in <form> — caller controls submit behavior.
 */
function ContactForm({
  contactForm,
  setContactForm,
  leadSources = [],
  repFirms = [],
  users = [],
  hideInfluenceFields = false,
  accounts = null,
}) {
  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setContactForm({ ...contactForm, [field]: value });
  };

  return (
    <>
      <div className="grid grid-cols-2">
        <div className="form-group">
          <label className="form-label">First Name *</label>
          <input
            type="text"
            className="form-input"
            value={contactForm.first_name || ''}
            onChange={set('first_name')}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Last Name</label>
          <input
            type="text"
            className="form-input"
            value={contactForm.last_name || ''}
            onChange={set('last_name')}
          />
        </div>
      </div>
      {Array.isArray(accounts) && (
        <div className="form-group">
          <label className="form-label" htmlFor="contact-form-account">Account *</label>
          <select
            id="contact-form-account"
            className="form-select"
            value={contactForm.account_id || ''}
            onChange={set('account_id')}
            required
          >
            <option value="">Select Account</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      )}
      <div className="grid grid-cols-2">
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-input"
            value={contactForm.email || ''}
            onChange={set('email')}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Phone</label>
          <input
            type="text"
            className="form-input"
            value={contactForm.phone || ''}
            onChange={set('phone')}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="form-group">
          <label className="form-label">Job Title</label>
          <input
            type="text"
            className="form-input"
            value={contactForm.job_title || ''}
            onChange={set('job_title')}
          />
        </div>
        <div className="form-group">
          <label className="form-label">LinkedIn URL</label>
          <input
            type="url"
            className="form-input"
            placeholder="https://linkedin.com/in/..."
            value={contactForm.linkedin_url || ''}
            onChange={set('linkedin_url')}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="form-group">
          <label className="form-label">City</label>
          <input
            type="text"
            className="form-input"
            value={contactForm.city || ''}
            onChange={set('city')}
          />
        </div>
        <div className="form-group">
          <label className="form-label">State</label>
          <input
            type="text"
            className="form-input"
            value={contactForm.state || ''}
            onChange={set('state')}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="form-group">
          <label className="form-label">Country</label>
          <input
            type="text"
            className="form-input"
            value={contactForm.country || ''}
            onChange={set('country')}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="form-group">
          <label className="form-label">Lead Source</label>
          <select
            className="form-select"
            value={contactForm.lead_source_id || ''}
            onChange={set('lead_source_id')}
          >
            <option value="">Select Lead Source</option>
            {leadSources.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="form-group">
          <label className="form-label">Rep Firm</label>
          <select
            className="form-select"
            value={contactForm.rep_firm_id || ''}
            onChange={set('rep_firm_id')}
          >
            <option value="">Select Rep Firm</option>
            {repFirms.map(rf => (
              <option key={rf.id} value={rf.id}>{rf.name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Owner</label>
          <select
            className="form-select"
            value={contactForm.owner_id || ''}
            onChange={set('owner_id')}
          >
            <option value="">Select Owner</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
      </div>
      {!hideInfluenceFields && (
        <>
          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Influence Role</label>
              <select
                className="form-select"
                value={contactForm.influence_role || ''}
                onChange={set('influence_role')}
              >
                <option value="">— not set —</option>
                <option value="DM">DM · Decision Maker</option>
                <option value="INF">INF · Influences DM</option>
                <option value="IM">IM · Implementer</option>
                <option value="CO">CO · Coach / Consultant</option>
                <option value="NI">NI · No Influence</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Social Style</label>
              <select
                className="form-select"
                value={contactForm.social_style || ''}
                onChange={set('social_style')}
              >
                <option value="">— not set —</option>
                <option value="DR">DR · Driver</option>
                <option value="EX">EX · Expressive</option>
                <option value="AY">AY · Analytic</option>
                <option value="AM">AM · Amiable</option>
                <option value="UN">UN · Unknown</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Relationship Status</label>
              <select
                className="form-select"
                value={contactForm.relationship_status || ''}
                onChange={set('relationship_status')}
              >
                <option value="">— not set —</option>
                <option value="green">Good, established trust</option>
                <option value="yellow">Met, need to establish trust</option>
                <option value="red">Limited or no relationship</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Disposition (−5 … +5)</label>
              <input
                type="number"
                className="form-input"
                min="-5"
                max="5"
                step="1"
                value={contactForm.disposition === '' || contactForm.disposition == null ? '' : contactForm.disposition}
                onChange={set('disposition')}
                placeholder="e.g. +2 supporter, -3 detractor"
              />
            </div>
          </div>
        </>
      )}
      <div className="grid grid-cols-2">
        <div className="form-group">
          <label className="checkbox-wrapper">
            <input
              type="checkbox"
              className="checkbox"
              checked={!!contactForm.active}
              onChange={set('active')}
            />
            Active
          </label>
        </div>
        <div className="form-group">
          <label className="checkbox-wrapper">
            <input
              type="checkbox"
              className="checkbox"
              checked={!!contactForm.market_to}
              onChange={set('market_to')}
            />
            Market To
          </label>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Notes</label>
        <textarea
          className="form-textarea"
          value={contactForm.notes || ''}
          onChange={set('notes')}
        />
      </div>
    </>
  );
}

export default ContactForm;
